import React, { useState, useEffect } from 'react';
import { api } from '../../api/nocode';
import { sendEmail } from '../../api/ses';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';
import { getCampStatus, formatDateRange } from '../../utils/campLogic';

function getCourseEmailHtml(userName, courseName, passType) {
  const isAap = passType === 'allaccess' || passType === 'evergreen-aap';
  const tierLabel = isAap ? 'All-Access Pass' : 'Student Access Pass';
  const benefitsText = isAap
    ? 'Your All-Access Pass includes full access to your Morning Lesson videos, Study Hall archives, live Midday Lab sessions, private podcast feeds, and the Self-Letter Studio.'
    : 'Your enrollment includes access to your Morning Lesson videos, Study Hall materials, and live Midday Lab sessions.';

  return `
    <div style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #fdfaf6; padding: 0;">
      <div style="text-align: center; padding: 40px 20px 20px;">
        <img src="https://www.crossandpen.com/uploads/images/1789473595942-564555159.png" alt="GospelWise Author Academy" style="max-width: 180px; height: auto;" />
      </div>
      <div style="padding: 0 40px 40px;">
        <div style="background: #ffffff; padding: 40px; border-top: 4px solid #2D5016; border-radius: 8px; color: #2c3e20; box-shadow: 0 4px 15px rgba(0,0,0,0.03);">
          <div style="text-align: center; margin-bottom: 20px;">
            <span style="background-color: #EAF1E6; color: #2D5016; font-size: 12px; font-weight: bold; padding: 4px 12px; border-radius: 20px; text-transform: uppercase; letter-spacing: 1px;">Course Access Granted</span>
          </div>
          <h2 style="color: #2D5016; margin-top: 0; margin-bottom: 16px; font-size: 24px; text-align: center;">Welcome to ${courseName}!</h2>
          <p style="font-size: 16px; line-height: 1.6; margin-bottom: 20px;">
            Hi ${userName || 'Author'},
          </p>
          <p style="font-size: 16px; line-height: 1.6; margin-bottom: 20px;">
            You have been granted complimentary access and are officially enrolled in <strong>${courseName}</strong> (${tierLabel}) at GospelWise Author Academy!
          </p>
          <p style="font-size: 16px; line-height: 1.6; margin-bottom: 25px;">
            ${benefitsText}
          </p>
          <div style="text-align: center; margin: 35px 0 25px;">
            <a href="https://academy.gospelwiseauthor.com/#/login" style="background-color: #2D5016; color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 6px; font-weight: bold; font-size: 16px; display: inline-block;">Go to Your Academy Hub &rarr;</a>
          </div>
        </div>
        <p style="margin-top: 30px; text-align: center; font-size: 12px; color: #a39b90;">
          &copy; 2026 Gospel-Wise Author Academy. All rights reserved.<br/>
          You received this email because course access was gifted to your account by an administrator.
        </p>
      </div>
    </div>
  `;
}

function ManageRunsModal({
  user,
  runs,
  sessions,
  passes,
  enrollments,
  onClose,
  onAccessUpdated
}) {
  const [userPasses, setUserPasses] = useState([]);
  const [userEnrollments, setUserEnrollments] = useState([]);
  const [processingRunId, setProcessingRunId] = useState(null);
  const [selectedPassTypes, setSelectedPassTypes] = useState({});
  const [sendEmailOnGift, setSendEmailOnGift] = useState(true);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    const matchingPasses = passes.filter(
      (p) => String(p.user_id) === String(user.id)
    );
    const matchingEnrollments = enrollments.filter(
      (e) => String(e.user_id) === String(user.id) || String(e.owner_id) === String(user.id)
    );

    setUserPasses(matchingPasses);
    setUserEnrollments(matchingEnrollments);

    const initialTypes = {};
    runs.forEach((r) => {
      const pass = matchingPasses.find((p) => String(p.run_id) === String(r.id));
      const enrollment = matchingEnrollments.find((e) => String(e.run_id) === String(r.id));
      initialTypes[r.id] = pass?.pass_type || enrollment?.pass_type || 'paid';
    });
    setSelectedPassTypes(initialTypes);
  }, [user, passes, enrollments, runs]);

  const isUserEnrolledInRun = (runId) => {
    const hasPass = userPasses.some((p) => String(p.run_id) === String(runId));
    if (hasPass) return true;
    return userEnrollments.some((e) => String(e.run_id) === String(runId));
  };

  const getPassForRun = (runId) => {
    return userPasses.find((p) => String(p.run_id) === String(runId)) || null;
  };

  const handleToggleRunAccess = async (run) => {
    const isEnrolled = isUserEnrolledInRun(run.id);
    const session = sessions.find((s) => String(s.id) === String(run.session_id));
    const courseTitle = session ? `${session.title || session.name} — ${run.title || run.name}` : (run.title || run.name || `Run #${run.id}`);
    const passType = selectedPassTypes[run.id] || 'paid';

    setProcessingRunId(run.id);
    setNotice(null);

    try {
      if (isEnrolled) {
        // Revoke access: remove from passes_gwaacademy and enrollments_gwaacademy
        const passRecords = userPasses.filter((p) => String(p.run_id) === String(run.id));
        for (const p of passRecords) {
          if (p.id) {
            await api.deleteUnscoped('passes_gwaacademy', p.id).catch(() => {});
          }
        }

        const enrollmentRecords = userEnrollments.filter((e) => String(e.run_id) === String(run.id));
        for (const e of enrollmentRecords) {
          if (e.id) {
            await api.deleteUnscoped('enrollments_gwaacademy', e.id).catch(() => {});
          }
        }

        setUserPasses((prev) => prev.filter((p) => String(p.run_id) !== String(run.id)));
        setUserEnrollments((prev) => prev.filter((e) => String(e.run_id) !== String(run.id)));

        setNotice({
          type: 'info',
          message: `Access to "${courseTitle}" revoked for ${user.name}.`
        });
      } else {
        // Grant access: create pass in passes_gwaacademy
        const newPassPayload = {
          user_id: String(user.id),
          run_id: String(run.id),
          session_id: String(run.session_id || ''),
          department_id: String(session?.department_id || ''),
          pass_type: passType,
          source: 'gifted',
          price_paid: 0,
          owner_id: 'admin',
          purchased_at: new Date().toISOString()
        };

        const passRes = await api.postUnscoped('passes_gwaacademy', newPassPayload);
        const createdPass = passRes?.data || { ...newPassPayload, id: passRes?.id || Date.now() };

        // Best effort sync to enrollments_gwaacademy
        await api.postUnscoped('enrollments_gwaacademy', {
          user_id: String(user.id),
          run_id: String(run.id),
          session_id: String(run.session_id || ''),
          department_id: String(session?.department_id || ''),
          pass_type: passType,
          status: 'active',
          starts_at: run.start_date ? `${run.start_date} 00:00:00` : null,
          owner_id: String(user.id)
        }).catch(() => {});

        setUserPasses((prev) => [...prev, createdPass]);

        let emailSent = false;
        if (sendEmailOnGift && user.email) {
          const emailSubject = `You've been given access to ${courseTitle}!`;
          const htmlContent = getCourseEmailHtml(user.name, courseTitle, passType);
          emailSent = await sendEmail(user.email, emailSubject, htmlContent);
        }

        setNotice({
          type: 'success',
          message: emailSent
            ? `Enrolled ${user.name} in "${courseTitle}" and sent notification email!`
            : `Enrolled ${user.name} in "${courseTitle}" successfully.`
        });
      }

      await onAccessUpdated();
    } catch (err) {
      console.error('Failed to toggle run access:', err);
      setNotice({
        type: 'error',
        message: err?.message || 'Failed to update access. Please try again.'
      });
    } finally {
      setProcessingRunId(null);
    }
  };

  const handlePassTypeChange = async (runId, newType) => {
    setSelectedPassTypes((prev) => ({ ...prev, [runId]: newType }));

    const existingPass = getPassForRun(runId);
    if (existingPass && existingPass.id) {
      setProcessingRunId(runId);
      try {
        await api.putUnscoped('passes_gwaacademy', existingPass.id, { pass_type: newType });
        setUserPasses((prev) =>
          prev.map((p) => (String(p.id) === String(existingPass.id) ? { ...p, pass_type: newType } : p))
        );
        await onAccessUpdated();
      } catch (err) {
        console.error('Failed to change pass type:', err);
      } finally {
        setProcessingRunId(null);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
      <Card className="my-8 w-full max-w-2xl space-y-5 border-2 border-forest-green shadow-2xl bg-white">
        <div className="flex items-start justify-between border-b border-tan pb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-1.5 bg-forest-green/10 rounded-md text-forest-green">
                <SafeIcon name="Gift" className="text-lg" />
              </span>
              <h2 className="text-xl font-bold text-dark-green">
                Manage Course &amp; Run Access
              </h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Assign or revoke specific session event runs for <strong className="text-gray-900">{user.name}</strong> ({user.email})
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-dark-green transition-colors p-1"
          >
            <SafeIcon name="X" className="text-xl" />
          </button>
        </div>

        {notice && (
          <div
            className={`p-3 rounded-md text-sm flex items-center gap-2 ${
              notice.type === 'success'
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                : notice.type === 'info'
                ? 'bg-blue-50 border border-blue-200 text-blue-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            <SafeIcon
              name={notice.type === 'success' ? 'CheckCircle' : notice.type === 'info' ? 'Info' : 'AlertCircle'}
              className="shrink-0 text-base"
            />
            <span>{notice.message}</span>
          </div>
        )}

        <div className="bg-amber/10 border border-amber/30 rounded-md p-3 flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-dark-green">
            <input
              type="checkbox"
              checked={sendEmailOnGift}
              onChange={(e) => setSendEmailOnGift(e.target.checked)}
              className="rounded border-amber text-forest-green focus:ring-forest-green"
            />
            <span>Send enrollment notification email to student when granting access</span>
          </label>
          <span className="text-[11px] text-muted">{user.email}</span>
        </div>

        <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
          {runs.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">
              No event runs available. Create runs in the Sessions manager first.
            </div>
          ) : (
            runs.map((run) => {
              const session = sessions.find((s) => String(s.id) === String(run.session_id));
              const isEnrolled = isUserEnrolledInRun(run.id);
              const isProcessing = processingRunId === run.id;
              const currentPassType = selectedPassTypes[run.id] || 'paid';
              const dateDisplay = formatDateRange(run.start_date, run.end_date);

              return (
                <div
                  key={run.id}
                  className={`p-4 rounded-lg border transition-all ${
                    isEnrolled
                      ? 'border-forest-green bg-[#F4F8F2]'
                      : 'border-gray-200 bg-white hover:border-tan'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-dark-green text-sm">
                          {run.title || run.name || `Run #${run.id}`}
                        </span>
                        {session && (
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-medium">
                            {session.title || session.name}
                          </span>
                        )}
                        {isEnrolled && (
                          <span className="text-xs bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                            <SafeIcon name="Check" className="text-[10px]" /> Enrolled
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-2">
                        {dateDisplay && <span>{dateDisplay}</span>}
                        {run.slug && (
                          <span className="font-mono text-dark-green bg-amber/20 px-1.5 py-0.2 rounded text-[10px]">
                            /hub/{run.slug}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 self-end sm:self-center">
                      {isEnrolled && (
                        <div className="flex items-center gap-1">
                          <label className="text-xs font-semibold text-gray-600 mr-1">Tier:</label>
                          <select
                            value={currentPassType}
                            onChange={(e) => handlePassTypeChange(run.id, e.target.value)}
                            disabled={isProcessing}
                            className="text-xs border border-gray-300 rounded px-2 py-1 bg-white text-gray-800 font-medium"
                          >
                            <option value="paid">Student Access (Paid)</option>
                            <option value="allaccess">All-Access Pass (AAP)</option>
                          </select>
                        </div>
                      )}

                      {!isEnrolled && (
                        <div className="flex items-center gap-1">
                          <select
                            value={currentPassType}
                            onChange={(e) => setSelectedPassTypes({ ...selectedPassTypes, [run.id]: e.target.value })}
                            className="text-xs border border-gray-300 rounded px-2 py-1 bg-white text-gray-800 font-medium"
                          >
                            <option value="paid">Grant Student Access</option>
                            <option value="allaccess">Grant All-Access</option>
                          </select>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => handleToggleRunAccess(run)}
                        disabled={isProcessing}
                        className={`h-7 w-12 shrink-0 rounded-full p-1 transition-colors relative ${
                          isEnrolled ? 'bg-forest-green' : 'bg-gray-300 hover:bg-gray-400'
                        } ${isProcessing ? 'opacity-60 cursor-not-allowed' : ''}`}
                        title={isEnrolled ? 'Click to revoke access' : 'Click to grant access'}
                      >
                        <span
                          className={`block h-5 w-5 rounded-full bg-white transition-transform ${
                            isEnrolled ? 'translate-x-5' : ''
                          }`}
                        />
                        {isProcessing && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <SafeIcon name="Loader" className="animate-spin text-xs text-white" />
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="flex justify-end gap-3 pt-3 border-t border-tan">
          <Button variant="outline" onClick={onClose}>
            Done
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [runs, setRuns] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [passes, setPasses] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [selectedRun, setSelectedRun] = useState('all');
  const [managingRunsUser, setManagingRunsUser] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    const [usersRes, settingsRes, runsRes, sessionsRes, enrollmentsRes, passesRes] = await Promise.all([
      api.get('students_gwaacademy'),
      api.get('settings_gwaacademy'),
      api.get('runs_gwaacademy').catch(() => []),
      api.get('sessions_gwaacademy').catch(() => []),
      api.getUnscoped('enrollments_gwaacademy').catch(() => []),
      // passes_gwaacademy is a run-scoped table in the data layer, so it must
      // be read unscoped here — this page has no event run selected, and
      // scoping it would silently return an empty list. passes_gwaacademy is
      // also the reliable student-to-run link: unlike enrollments_gwaacademy
      // (whose legacy single-column unique constraints on user_id/run_id
      // reject every row after the very first one for a given student or
      // run), passes rows are created for every Skillplate purchase without
      // that limitation.
      api.getUnscoped('passes_gwaacademy').catch(() => [])
    ]);
    setUsers(Array.isArray(usersRes) ? usersRes : usersRes.data || []);
    setSettings(Array.isArray(settingsRes) ? settingsRes[0] : settingsRes?.data?.[0] || {});
    setRuns(Array.isArray(runsRes) ? runsRes : runsRes.data || []);
    setSessions(Array.isArray(sessionsRes) ? sessionsRes : sessionsRes.data || []);
    setEnrollments(Array.isArray(enrollmentsRes) ? enrollmentsRes : enrollmentsRes.data || []);
    setPasses(Array.isArray(passesRes) ? passesRes : passesRes.data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const toggleAllAccess = async (user) => {
    let newPass = user.pass_type === 'allaccess' ? 'paid' : 'allaccess';
    
    // Handle evergreen types
    if (user.pass_type === 'evergreen') newPass = 'evergreen-aap';
    else if (user.pass_type === 'evergreen-aap') newPass = 'paid';

    await api.put('students_gwaacademy', user.id, { pass_type: newPass });
    fetchUsers();
  };

  const toggleEarlyAccess = async (user) => {
    const newEarlyAccess = user.early_access == 1 ? 0 : 1;
    await api.put('students_gwaacademy', user.id, { early_access: newEarlyAccess });
    fetchUsers();
  };

  const removeUser = async (user) => {
    if (!window.confirm(`Are you sure you want to completely remove ${user.name}? This action cannot be undone.`)) return;
    
    setDeletingId(user.id);
    try {
      await api.delete('students_gwaacademy', user.id);
      fetchUsers();
    } catch (err) {
      console.error("Failed to delete user", err);
      alert("Failed to remove user. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  // A student "belongs" to a run if either a passes_gwaacademy row (the
  // reliable link) or an enrollments_gwaacademy row (legacy, best-effort)
  // connects their id to that run_id.
  const userInRun = (userId, runId) => {
    const matchesPass = passes.some(p =>
      String(p.user_id) === String(userId) && String(p.run_id) === String(runId)
    );
    if (matchesPass) return true;

    return enrollments.some(e =>
      (String(e.user_id) === String(userId) || String(e.owner_id) === String(userId)) &&
      String(e.run_id) === String(runId)
    );
  };

  const getRunStudentCount = (runId) => {
    const studentIds = new Set();
    passes.forEach(p => {
      if (String(p.run_id) === String(runId) && p.user_id) {
        studentIds.add(String(p.user_id));
      }
    });
    enrollments.forEach(e => {
      if (String(e.run_id) === String(runId)) {
        const id = e.user_id || e.owner_id;
        if (id) studentIds.add(String(id));
      }
    });
    return studentIds.size;
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name?.toLowerCase().includes(search.toLowerCase()) || 
                          u.email?.toLowerCase().includes(search.toLowerCase());
    
    if (!matchesSearch) return false;

    if (selectedRun !== 'all') {
      return userInRun(u.id, selectedRun);
    }

    return true;
  });

  const getPassBadge = (type) => {
    switch (type) {
      case 'allaccess':
      case 'evergreen-aap':
        return 'bg-amber/20 text-amber';
      case 'paid':
        return 'bg-gwa-blue/20 text-gwa-blue font-bold';
      case 'evergreen':
        return 'bg-forest-green/20 text-forest-green';
      default:
        return 'bg-gray-100 text-gray-500';
    }
  };

  // Funnel calculations applied to All Students
  const now = new Date();
  const oneWeekAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);

  const regsThisWeek = users.filter(u => {
    if (u.created_at) {
      const created = new Date(u.created_at);
      if (!isNaN(created.getTime()) && created >= oneWeekAgo) return true;
    }
    if (!u.trail_start_date) return false;
    const [y, m, d] = u.trail_start_date.split('-');
    const start = new Date(y, m - 1, d);
    return start >= oneWeekAgo;
  }).length;

  const dayDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 'Completed': 0 };
  users.forEach(u => {
    const { currentTrailDay } = getCampStatus(u, settings);
    if (currentTrailDay >= 1 && currentTrailDay <= 5) {
      dayDistribution[currentTrailDay]++;
    } else if (currentTrailDay > 5) {
      dayDistribution['Completed']++;
    }
  });

  const day5Completions = dayDistribution['Completed'];
  const aapConversions = users.filter(u => ['allaccess', 'evergreen-aap'].includes(u.pass_type)).length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Student & User Management</h1>
          <div className="flex items-center">
            <select
              value={selectedRun}
              onChange={(e) => setSelectedRun(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1 text-sm bg-white text-gray-700 font-medium"
            >
              <option value="all">All Students</option>
              {runs.map(r => {
                const s = sessions.find(sess => String(sess.id) === String(r.session_id));
                const count = getRunStudentCount(r.id);
                return (
                  <option key={r.id} value={r.id}>
                    {r.title || r.name || `Run #${r.id}`} {s ? `(${s.title || s.name})` : ''} — {count} student{count === 1 ? '' : 's'}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        <div className="w-64">
          <Input 
            placeholder="Search students..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-forest-green text-white">
          <div className="text-sm opacity-80 uppercase tracking-wider font-bold">New This Week</div>
          <div className="text-3xl font-bold mt-1">{regsThisWeek}</div>
        </Card>
        
        <Card className="p-4 border-forest-green/20">
          <div className="text-sm text-gray-500 uppercase tracking-wider font-bold">On Trail (D1-D5)</div>
          <div className="text-3xl font-bold text-gray-800 mt-1">
            {dayDistribution[1] + dayDistribution[2] + dayDistribution[3] + dayDistribution[4] + dayDistribution[5]}
          </div>
          <div className="flex gap-2 text-xs text-gray-500 mt-2 font-mono">
            <span>D1: {dayDistribution[1]}</span>
            <span>D2: {dayDistribution[2]}</span>
            <span>D3: {dayDistribution[3]}</span>
            <span>D4: {dayDistribution[4]}</span>
            <span>D5: {dayDistribution[5]}</span>
          </div>
        </Card>

        <Card className="p-4 border-forest-green/20">
          <div className="text-sm text-gray-500 uppercase tracking-wider font-bold">Completions</div>
          <div className="text-3xl font-bold text-gray-800 mt-1">{day5Completions}</div>
          <div className="text-xs text-gray-400 mt-1">Past Day 5</div>
        </Card>

        <Card className="p-4 bg-amber/10 border-amber/20">
          <div className="text-sm text-amber-700 uppercase tracking-wider font-bold">AAP Conversions</div>
          <div className="text-3xl font-bold text-amber-600 mt-1">{aapConversions}</div>
          <div className="text-xs text-amber-600/70 mt-1 font-medium">
            {users.length > 0 ? Math.round((aapConversions / users.length) * 100) : 0}% of cohort
          </div>
        </Card>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Pass Type</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Start Date</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Genre / Stage</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="6" className="px-6 py-10 text-center text-gray-400">Loading students...</td></tr>
              ) : filteredUsers.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-10 text-center text-gray-400">No students found</td></tr>
              ) : filteredUsers.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-bold text-gray-900">{user.name}</div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 rounded-full capitalize">{user.role}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-bold rounded-full uppercase ${getPassBadge(user.pass_type)}`}>
                      {user.pass_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {user.trail_start_date || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {user.genre || '-'} / {user.journey_stage?.replace('_', ' ') || '-'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end items-center gap-2">
                      <Button
                        variant="outline"
                        className="text-xs py-1 px-2 border-forest-green text-forest-green hover:bg-forest-green hover:text-white"
                        onClick={() => setManagingRunsUser(user)}
                        title="Gift course access or toggle event runs"
                      >
                        <SafeIcon name="Gift" className="text-xs mr-1" />
                        Gift / Runs
                      </Button>
                      <Button
                        variant="outline"
                        className={`text-xs py-1 px-2 border ${user.early_access == 1 ? 'border-amber text-amber hover:bg-amber hover:text-white' : 'border-forest-green text-forest-green hover:bg-forest-green hover:text-white'}`}
                        onClick={() => toggleEarlyAccess(user)}
                        title={user.early_access == 1 ? 'Revoke Early Access' : 'Grant Early Access'}
                      >
                        <SafeIcon name={user.early_access == 1 ? "Lock" : "Unlock"} className="text-sm" />
                      </Button>
                      <Button 
                        variant={['allaccess', 'evergreen-aap'].includes(user.pass_type) ? 'outline' : 'secondary'} 
                        className="text-xs py-1"
                        onClick={() => toggleAllAccess(user)}
                      >
                        {['allaccess', 'evergreen-aap'].includes(user.pass_type) ? 'Downgrade' : 'Upgrade'}
                      </Button>
                      <Button
                        variant="outline"
                        className="text-xs py-1 px-2 border-red-200 text-red-600 hover:text-white hover:bg-red-600 hover:border-red-600"
                        onClick={() => removeUser(user)}
                        disabled={deletingId === user.id}
                        title="Remove Student"
                      >
                        {deletingId === user.id ? (
                          <SafeIcon name="Loader" className="animate-spin text-sm" />
                        ) : (
                          <SafeIcon name="Trash2" className="text-sm" />
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {managingRunsUser && (
        <ManageRunsModal
          user={managingRunsUser}
          runs={runs}
          sessions={sessions}
          passes={passes}
          enrollments={enrollments}
          onClose={() => setManagingRunsUser(null)}
          onAccessUpdated={fetchUsers}
        />
      )}
    </div>
  );
}